import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { T, IMAGES } from "@/content/site";
import { api } from "@/lib/api";

const F = (v) => (v ? "filled" : "");

export default function Distributors() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", whatsapp: "", company_type: "", region: "", channels: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/distributor", form);
      toast.success(t(T.distributors.success));
      setForm({ name: "", company: "", email: "", country: "", whatsapp: "", company_type: "", region: "", channels: "", message: "" });
    } catch {
      toast.error(t({ es: "No pudimos enviar tu solicitud.", en: "We couldn't send your request." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="distributors" data-testid="distributors-section" className="relative bg-[#0B0B0B] py-[140px] md:py-[180px] border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]">
        <img src={IMAGES.agaveField} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10 items-end mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
              {t(T.distributors.overline)}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-white text-[40px] md:text-[64px] leading-[1] tracking-[-0.01em]"
            >
              {t(T.distributors.title)}
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.1 }}
              className="text-[color:var(--sacrvm-text-muted)] text-[16px] md:text-[17px] leading-[1.75] font-serif italic"
            >
              {t(T.distributors.body)}
            </motion.p>
          </div>
        </div>

        {/* Regions strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border-t border-white/10">
          {T.distributors.regions.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="border-b md:border-b-0 md:border-r border-white/10 last:border-r-0 px-4 py-8 text-center hover:bg-white/[0.03] transition-colors"
              data-testid={`region-${i}`}
            >
              <div className="font-serif text-[color:var(--sacrvm-gold)] text-[16px] mb-2">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-serif text-white text-[16px] md:text-[18px] leading-tight">{t(r)}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-5">{t({ es: "Formulario B2B", en: "B2B form" })}</div>
            <p className="text-[color:var(--sacrvm-text-muted)] text-[15px] leading-[1.7]">
              {t({
                es: "Cuéntanos sobre tu operación: canales, volumen anual estimado y territorios. Nuestro equipo internacional te responde en 24 horas.",
                en: "Tell us about your operation: channels, estimated annual volume and territories. Our international team replies within 24 hours.",
              })}
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <form onSubmit={submit} data-testid="distributor-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <div className={"field " + F(form.name)}>
                <label>{t({ es: "Nombre", en: "Name" })}</label>
                <input data-testid="dist-name" value={form.name} onChange={set("name")} required />
              </div>
              <div className={"field " + F(form.company)}>
                <label>{t({ es: "Empresa", en: "Company" })}</label>
                <input data-testid="dist-company" value={form.company} onChange={set("company")} required />
              </div>
              <div className={"field " + F(form.email)}>
                <label>{t({ es: "Correo", en: "Email" })}</label>
                <input data-testid="dist-email" type="email" value={form.email} onChange={set("email")} required />
              </div>
              <div className={"field " + F(form.country)}>
                <label>{t({ es: "País", en: "Country" })}</label>
                <input data-testid="dist-country" value={form.country} onChange={set("country")} />
              </div>
              <div className={"field " + F(form.whatsapp)}>
                <label>WhatsApp</label>
                <input data-testid="dist-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} />
              </div>
              <div className={"field " + F(form.company_type)}>
                <label>{t({ es: "Tipo (Distribuidor · Importador · Retail)", en: "Type (Distributor · Importer · Retail)" })}</label>
                <input data-testid="dist-type" value={form.company_type} onChange={set("company_type")} />
              </div>
              <div className={"field " + F(form.region)}>
                <label>{t(T.distributors.fields.region)}</label>
                <input data-testid="dist-region" value={form.region} onChange={set("region")} />
              </div>
              <div className={"field " + F(form.channels)}>
                <label>{t({ es: "Canales (On-trade, Off-trade, Duty-Free…)", en: "Channels (On-trade, Off-trade, Duty-Free…)" })}</label>
                <input data-testid="dist-channels" value={form.channels} onChange={set("channels")} />
              </div>
              <div className={"field md:col-span-2 " + F(form.message)}>
                <label>{t({ es: "Mensaje", en: "Message" })}</label>
                <textarea data-testid="dist-message" rows={2} value={form.message} onChange={set("message")} />
              </div>
              <div className="md:col-span-2 mt-8">
                <button data-testid="dist-submit" disabled={loading} className="btn-sharp gold">
                  {loading ? t({ es: "Enviando…", en: "Sending…" }) : t(T.distributors.submit)}
                  <ArrowRight size={14} className="arrow" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
